import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import InputField from 'components/InputField';
import validations from 'validations';
import { Button, Loader } from 'ncoded-component-library';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import {
  getJoinedWordsWithComma,
  getUserFullNameOrEmail,
  getCheckedValuesInObject,
} from 'utils';

import './MergeBuySideGroups.styles.scss';

type MergeBuySideGroupsProps = {
  className?: string;
};

type MergeBuySideGroupsFormValues = {
  buySideGroupName: string;
  selectedGroupIds: Record<string, boolean>;
};

const MergeBuySideGroups: React.ForwardRefRenderFunction<
  SideDrawerRef,
  MergeBuySideGroupsProps
> = (props, ref) => {
  const { className } = props;

  const classes = classNames('merge-buy-side-groups', className);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);
  const { buySideGroups, setBuySideGroups, setSellSideMembers } =
    useContext(MembersContext);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { t } = useTranslation();

  const groupOptions = useMemo(
    () =>
      buySideGroups.map(({ _id, name, dataRoomMembers }) => ({
        value: _id,
        label: (
          <p>
            <span>{name}</span>
            <span>
              {getJoinedWordsWithComma(
                dataRoomMembers.map(getUserFullNameOrEmail),
              )}
            </span>
          </p>
        ),
      })),
    [buySideGroups],
  );

  const getSelectedCount = useCallback(
    (selectedGroupIds: Record<string, boolean>) =>
      Object.values(selectedGroupIds || {}).filter(Boolean).length,
    [],
  );

  const onSubmit = useCallback(
    async (values: MergeBuySideGroupsFormValues) => {
      const { buySideGroupName, selectedGroupIds } = values;

      const buySideGroupIds = getCheckedValuesInObject(selectedGroupIds);

      try {
        const { data } = await dataRoomApi.members.mergeBuySideGroups(
          dataRoomId,
          { buySideGroupName, buySideGroupIds },
        );

        const { sellSideMembers, buySideGroups: updatedBuySideGroups } = data;
        setSellSideMembers(sellSideMembers);
        setBuySideGroups(updatedBuySideGroups);
        drawer?.setOpen(false);

        popSuccess(t('successfullyMergedBuySideGroups'));
      } catch (e) {
        popServerError(e);
      }
    },
    [
      dataRoomId,
      drawer,
      popServerError,
      popSuccess,
      setBuySideGroups,
      setSellSideMembers,
      t,
    ],
  );

  return (
    <SideDrawer
      ref={drawerRef}
      title={t('mergeBuySideGroups')}
      className={classes}
    >
      {drawer?.open && (
        <Form
          onSubmit={onSubmit}
          render={({
            invalid,
            submitting,
            values: { selectedGroupIds },
            handleSubmit,
          }) => {
            const selectedCount = getSelectedCount(selectedGroupIds);

            return (
              <form onSubmit={handleSubmit}>
                {submitting && <Loader />}

                <p>{t('selectGroupsToMerge')}</p>

                <CheckboxFieldGroup
                  name="selectedGroupIds"
                  options={groupOptions}
                />

                <Field
                  name="buySideGroupName"
                  label={t('newGroupName')}
                  component={InputField}
                  required
                  validate={validations.required()}
                />

                <div className="actions">
                  <Button variant="link" onClick={() => drawer?.setOpen(false)}>
                    {t('cancel')}
                  </Button>

                  <Button
                    type="submit"
                    disabled={submitting || invalid || selectedCount < 2}
                  >
                    {t('save')}
                  </Button>
                </div>
              </form>
            );
          }}
        />
      )}
    </SideDrawer>
  );
};

export default forwardRef(MergeBuySideGroups);
